import React, { useState } from 'react';
import './Interview.css';

const questions = [
  "自己紹介をお願いします。",
  "あなたの長所は何ですか？",
  "あなたの長所と短所を教えてください",
  "自分を一言で表すと？",
  "なぜこの会社を志望したのですか？",
  "この職種を目指す理由は何ですか？",
  "当社のどの点に魅力を感じましたか？",
  "どのような資格・スキルを持っていますか？",
  "チームで取り組んだ経験はありますか？",
  "プレッシャーや困難にどう対処しますか？",
  "ミスをした時、どのように対応しますか？",
  "上司や先輩と意見が合わない時、どうしますか？",
  "何か質問はありますか？"
];

const translations = [
  "Please introduce yourself.",
  "What are your strengths?",
  "What are your strengths and weaknesses?",
  "Describe yourself in one word.",
  "Why did you apply to this company?",
  "Why do you aim for this role?",
  "What aspects of our company attract you?",
  "What qualifications or skills do you have?",
  "Do you have experience working in a team?",
  "How do you deal with pressure and challenges?",
  "How do you handle mistakes?",
  "What do you do when you disagree with a superior or senior?",
  "Do you have any questions for us?"
];

const Interview = () => {
  const [step, setStep] = useState('start');
  const [index, setIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [translatedAnswer, setTranslatedAnswer] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isListening, setIsListening] = useState(false);

  const speak = (text, callback) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'ja-JP';
    utterance.onend = () => callback && callback();
    speechSynthesis.speak(utterance);
  };

  const startInterview = () => {
    setStep('greeting');
    speak("こんにちは！模擬面接を始めましょう！", () => {
      askQuestion();
    });
  };

  const askQuestion = () => {
    const question = questions[index];
    setStep('asking');
    speak(question, () => {
      listenToAnswer();
    });
  };

  const listenToAnswer = () => {
    setStep('listening');
    setIsListening(true);

    const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
    recognition.lang = 'ja-JP';
    recognition.interimResults = false;

    recognition.start();

    recognition.onresult = (event) => {
      const result = event.results[0][0].transcript;
      setUserAnswer(result);
      setIsListening(false);
      evaluateAnswer(result);
    };

    recognition.onerror = (e) => {
      console.error('Recognition error:', e.error);
      setIsListening(false);
      setFeedback("⚠️ フィードバック取得中にエラーが発生しました。\n⚠️ An error occurred while capturing your answer.");
      setStep('feedback');
    };
  };

  const evaluateAnswer = (answer) => {
    // Store feedback in localStorage
    try {
      const feedbackData = {
        question: questions[index],
        answer: answer,
        timestamp: new Date().toISOString(),
        feedback: `Japanese Feedback: 良い回答です！\nEnglish Feedback: Good answer!`
      };
      
      const existingFeedback = JSON.parse(localStorage.getItem('sunlankaFeedback') || '[]');
      existingFeedback.push(feedbackData);
      localStorage.setItem('sunlankaFeedback', JSON.stringify(existingFeedback));
      
      // Extract feedback
      const jap = feedbackData.feedback.match(/Japanese Feedback:\s*(.*)/)?.[1] || "不明なフィードバック";
      const eng = feedbackData.feedback.match(/English Feedback:\s*(.*)/)?.[1] || "Unknown feedback.";

      // Set translated answer (for now, just show the original answer)
      setTranslatedAnswer(answer);

      const fullFeedback = `🇯🇵 ${jap}\n\n🇺🇸 ${eng}`;
      setFeedback(fullFeedback);
      speak(jap);
      setStep('feedback');
      
    } catch (err) {
      console.error('Feedback error:', err);
      setFeedback("⚠️ フィードバック取得中にエラーが発生しました。\n⚠️ An error occurred while evaluating your answer.");
      setStep('feedback');
    }
  };

  const nextOrRepeat = () => {
    if (feedback.includes("正しい") || feedback.toLowerCase().includes("correct")) {
      if (index + 1 < questions.length) {
        setIndex(index + 1);
        askQuestion();
      } else {
        setStep('done');
        speak("模擬面接は終了しました。お疲れ様でした！");
      }
    } else {
      askQuestion();
    }
  };

  return (
    <div className="mock-interview-container">
      <h2>🗣️ Japanese Mock Interview</h2>

      {step === 'start' && (
        <>
          <p className="question">Are you ready for your interview?</p>
          <p className="instruction">Tap the mic to start</p>
        </>
      )}

      {step === 'greeting' && (
        <p className="question">Greeting...</p>
      )}

      {step === 'asking' && (
        <p className="question">
          <strong>質問 {index + 1} (Question):</strong><br />
          {questions[index]}<br />
          <span className="text-sm">({translations[index]})</span>
        </p>
      )}

      {step === 'listening' && (
        <p className="instruction">🎤 Listening... Please answer in Japanese</p>
      )}

      {step === 'feedback' && (
        <>
          <p className="question">
            <strong>質問 (Question):</strong><br />
            {questions[index]}<br />
            <span className="text-sm">({translations[index]})</span>
          </p>
          <p className="question">
            <strong>あなたの答え (Your Answer):</strong><br />
            🇯🇵 {userAnswer}<br />
            🇺🇸 {translatedAnswer}
          </p>
          <p className="feedback">
            <strong>🗨️ フィードバック (Feedback):</strong><br />
            {feedback.split('\n').map((line, i) => (
              <span key={i}>{line}<br /></span>
            ))}
          </p>
          <p className="instruction">Tap mic to continue</p>
        </>
      )}

      {step === 'done' && (
        <p className="question">🎉 面接は完了しました。お疲れ様でした！<br />Interview complete. Well done!</p>
      )}

      <div className="mic-wrapper" onClick={() => {
        if (step === 'start') startInterview();
        else if (step === 'feedback') nextOrRepeat();
      }}>
        <div className={`mic-circle ${isListening ? 'listening' : ''}`}>
          <img src="/mic.svg" alt="Mic" className="mic-icon" />
        </div>
      </div>
    </div>
  );
};

export default Interview;
